"use client";

import { useEffect, useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import {
  useGetOnboardingStatus,
  type GetOnboardingStatusQueryResult,
} from "@/lib/api/generated";
import { useAuth } from "@/features/auth";

let _onboardingChecked = false;

type ApiData<T> = T extends { data: infer D } ? D : T;
type OnboardingStatus = ApiData<GetOnboardingStatusQueryResult>;

export function resetOnboardingCheck() {
  _onboardingChecked = false;
}

export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  const onboardingQuery = useGetOnboardingStatus({
    query: {
      enabled: !!user && !_onboardingChecked,
      retry: false,
    },
  });
  const onboardingStatus = onboardingQuery.data as OnboardingStatus | undefined;

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      router.replace("/login");
      return;
    }
    if (_onboardingChecked) return;

    // Wait until the status request settles. Otherwise a stale cached `completed: false`
    // (e.g. from an earlier /projects visit) can trigger redirect back to /onboarding
    // before the refetch returns `completed: true` after the user finishes the flow.
    if (onboardingQuery.isPending || onboardingQuery.isFetching) return;

    if (onboardingStatus) {
      if (!onboardingStatus.completed) {
        router.replace("/onboarding");
      } else {
        _onboardingChecked = true;
      }
    } else if (onboardingQuery.isError) {
      _onboardingChecked = true;
    }
  }, [
    user,
    isLoading,
    router,
    onboardingStatus,
    onboardingQuery.isError,
    onboardingQuery.isPending,
    onboardingQuery.isFetching,
  ]);

  if (!mounted || isLoading || !user) {
    return null;
  }

  return <>{children}</>;
}
